import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { cn } from "../../../lib/utils";

interface AnimatedCircularProgressBarProps {
  max: number;
  value: number;
  min?: number;
  gaugePrimaryColor?: string;
  gaugeSecondaryColor?: string;
  className?: string;
  size?: number;
  strokeWidth?: number;
  duration?: number;
  delay?: number;
  showValue?: boolean;
  formatValue?: (value: number) => string;
}

export const AnimatedCircularProgressBar: React.FC<AnimatedCircularProgressBarProps> = ({
  max = 100,
  min = 0,
  value,
  gaugePrimaryColor = "#3b82f6",
  gaugeSecondaryColor = "#e5e7eb",
  className,
  size = 120,
  strokeWidth = 8,
  duration = 2,
  delay = 0,
  showValue = true,
  formatValue = (val) => `${Math.round(val)}%`,
}) => {
  const [animatedValue, setAnimatedValue] = useState(min);
  const normalizedValue = Math.min(Math.max(value, min), max);
  const percentage = ((normalizedValue - min) / (max - min)) * 100;
  
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  useEffect(() => {
    const timer = setTimeout(() => {
      const startTime = Date.now();
      const startValue = animatedValue;
      const endValue = normalizedValue;
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / (duration * 1000), 1);
        const easeOutProgress = 1 - Math.pow(1 - progress, 3);
        
        const currentValue = startValue + (endValue - startValue) * easeOutProgress;
        setAnimatedValue(currentValue);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      
      animate();
    }, delay * 1000);

    return () => clearTimeout(timer);
  }, [normalizedValue, duration, delay]);

  return (
    <div className={cn("relative flex items-center justify-center", className)}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={gaugeSecondaryColor}
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        
        {/* Progress circle */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={gaugePrimaryColor}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ 
            strokeDashoffset: circumference - ((animatedValue - min) / (max - min)) * circumference 
          }}
          transition={{ duration: duration, delay, ease: "easeOut" }}
        />
      </svg>
      
      {/* Value display */}
      {showValue && (
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: delay + duration * 0.5 }}
        >
          <span className="text-sm font-semibold text-gray-700">
            {formatValue(animatedValue)}
          </span>
        </motion.div>
      )}
    </div>
  );
};